import React, { useEffect, useRef, useState } from "react";
import { MdCancel, MdOutlineCloudUpload } from "react-icons/md";
import {
  useGetUserByIdQuery,
  useRegisterMutation,
  useUpgradeRoleMutation,
} from "../../redux/userApi";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { toast } from "react-toastify";
import { showErrorToast } from "../../utils/Utils";

const CreateUser = () => {
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "mediaTeam",
    password: "",
  });

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data, isLoading: isUserLoading } = useGetUserByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  useEffect(() => {
    if (isEditMode && data) {
      setFormData({
        email: data?.user?.email,
        name: data?.user?.name,
        role: data?.user?.role,
      });

      if (data?.user?.profile) {
        setImage({
          src: data?.user?.profile,
          file: null,
        });
      }
    }
  }, [isEditMode, data]);

  const [register, { isLoading }] = useRegisterMutation();
  const [upgradeRole, { isLoading: isUpgradeLoading }] =
    useUpgradeRoleMutation();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({
    email: "",
    name: "",
    role: "",
    password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      if (img.width !== img.height || img.width > 1000) {
        setError("Image must be square and no larger than 1000x1000 pixels.");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      const newImage = {
        src: objectUrl,
        file,
      };

      setImage(newImage);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  //   const handleEmailChange = (e) => {
  //     const value = e.target.value;
  //     setFormData({
  //       ...formData,
  //       email: value,
  //     });

  //     const newErrors = { ...errors };

  //     if (!value.trim()) {
  //       newErrors.email = "Email is required.";
  //     } else if (!/\S+@\S+\.\S+/.test(value)) {
  //       newErrors.email = "Please enter a valid email address.";
  //     } else {
  //       newErrors.email = ''; // Clear error if valid email
  //     }

  //     setErrors(newErrors);
  //   };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};
    setError("");

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
      isValid = false;
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
      isValid = false;
    }
    if (!isEditMode) {
      if (!formData.password.trim()) {
        newErrors.password = "Password is required.";
        isValid = false;
      }
      if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters long.";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const userData = new FormData();
    userData.append("role", formData.role);
    userData.append("name", formData.name);
    userData.append("email", formData.email);
    userData.append("password", formData.password);
    if (image?.file) {
      userData.append("profileImage", image?.file);
    }
    let upgradeRoleData = null;
    if (isEditMode) {
      upgradeRoleData = {
        id,
        role: formData.role,
      };
    }

    try {
      let response = null;
      if (isEditMode) {
        response = await upgradeRole(upgradeRoleData).unwrap();
      } else {
        response = await register(userData).unwrap();
      }

      if (response.success) {
        toast.success(response.message);
        navigate("/users");
      }
    } catch (err) {
      console.error("users error:", err);
      showErrorToast(err);
    }
  };

  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <div className="flex items-center justify-between">
        <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
          {isEditMode ? "Update" : "Add"} User
        </h1>
      </div>
      {isUserLoading ? (
        <div className="flex items-center gap-4">
          <div className=" w-[35%] bg-[#efefef] animate-pulse h-[20rem] rounded-xl ">
            {" "}
          </div>
          <div className=" w-[65%] flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>

            <div className="flex flex-col gap-1 ">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>
            <div className="flex flex-col gap-1 ">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>
          </div>
        </div>
      ) : (
        <form className="flex items-center md:flex-row flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]">
          <div className="xl:w-[35%] md:w-[40%] w-full ">
            <div
              className="w-full xl:h-[21.5rem] lg:h-[18rem] h-[14rem] border border-[#00000033] lg:p-4 p-3 lg:rounded-xl rounded-lg relative "
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {!image && (
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer  flex flex-col md:gap-2 gap-1 items-center justify-center h-full w-full "
                >
                  <MdOutlineCloudUpload className="text-[#00643b] lg:size-10 md:size-8 size-6" />
                  <p className="text-center font-semibold text-[#0B0B0B] ">
                    Drag your file(s) or{" "}
                    <span className=" text-[#00643b]  ">browse</span>
                  </p>

                  <p className="text-[#6D6D6D] text-center lg:text-sm text-[12px]">
                    Max 10 MB files are allowed
                  </p>
                  <p className="text-[#6D6D6D] text-center lg:text-sm text-[12px]">
                    Recommended profile image size: max 1000×1000 pixels, 1:1
                    aspect ratio.
                  </p>
                </label>
              )}

              <input
                id="file-upload"
                type="file"
                accept="image/*"
                disabled={isEditMode ? true : false}
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />

              {image && (
                <div className="absolute inset-0 flex justify-center items-center p-4">
                  <img
                    src={image.src}
                    alt="Upload Preview"
                    className=" object-cover w-full h-full md:rounded-[1rem] rounded-lg max-w-full max-h-full"
                  />
                  {!isEditMode && (
                    <button
                      onClick={() => handleDelete()}
                      className="absolute top-2 right-2 bg-white rounded-full  "
                    >
                      <MdCancel className="lg:size-8 size-6 text-red-500" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {error && (
              <p className="text-red-500 md:text-sm text-[12px] ">{error}</p>
            )}
          </div>
          <div className="xl:w-[65%] md:w-[60%] w-full flex flex-col lg:gap-4 md:gap-3 gap-2">
            <div className="flex flex-col gap-1">
              <label className="font-semibold">Email</label>
              <input
                type="text"
                name="email"
                placeholder="Enter Email"
                disabled={isEditMode ? true : false}
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.email && (
                <p className="text-red-500 md:text-sm text-[12px] ">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-semibold">Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Name"
                value={formData.name}
                disabled={isEditMode ? true : false}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.name && (
                <p className="text-red-500 md:text-sm text-[12px] ">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-semibold">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              >
                <option value="" disabled>
                  Select
                </option>
                <option value="admin">Admin</option>
                <option value="mediaTeam">Media Team</option>
                <option value="suspended">Suspended</option>
              </select>
              {/* {errors.role && (
              <p className="text-red-500 text-sm ">{errors.role}</p>
            )} */}
            </div>

            {!isEditMode && (
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter Password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
                />
                {errors.password && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {errors.password}
                  </p>
                )}
              </div>
            )}
          </div>
        </form>
      )}
      {isUserLoading ? (
        <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
      ) : (
        <button
          onClick={(e) => handleSubmit(e)}
          disabled={isLoading || isUpgradeLoading ? true : false}
          className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 ml-auto px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
        >
          {isEditMode ? (
            isUpgradeLoading ? (
              <ButtonLoading text="Update..." />
            ) : (
              "Update"
            )
          ) : isLoading ? (
            <ButtonLoading text="Add..." />
          ) : (
            "Add"
          )}
        </button>
      )}
    </div>
  );
};

export default CreateUser;
